import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import './CookieConsent.css';
import { getStoredConsent, saveConsent, applyConsent, isMarketingPath, logConsentMetric } from '../utils/consent';

// Compact cookie-consent bar. Renders only on public marketing pages, and only
// until the visitor decides. Accept and Reject are given equal weight so the
// banner stays GDPR-compliant while keeping the higher-converting simple layout.
const CookieConsent = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const shownLoggedRef = useRef(false);

  useEffect(() => {
    if (!isMarketingPath(location.pathname)) {
      setVisible(false);
      return;
    }
    setVisible(!getStoredConsent());
  }, [location.pathname]);

  // Log a cookieless impression the first time the banner is shown, so the
  // accept/reject counts have a denominator.
  useEffect(() => {
    if (visible && !shownLoggedRef.current) {
      shownLoggedRef.current = true;
      logConsentMetric('shown');
    }
  }, [visible]);

  // Reopen the banner when the footer "Cookie settings" link is clicked, so
  // visitors can change or withdraw a previous choice.
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener('cl:open-cookie-settings', reopen);
    return () => window.removeEventListener('cl:open-cookie-settings', reopen);
  }, []);

  if (!visible) return null;

  const decide = (grant) => {
    logConsentMetric(grant ? 'accept' : 'reject');
    const saved = saveConsent({ analytics: grant, marketing: grant });
    applyConsent(saved, location.pathname);
    setVisible(false);
  };

  return (
    <div className="cc-root" role="dialog" aria-live="polite" aria-label="Cookie settings">
      <div className="cc-panel">
        <div className="cc-body">
          <h2 className="cc-title">Cookie Settings</h2>
          <p className="cc-text">
            We use cookies to enhance your experience, analyze site traffic and deliver personalized
            content. Read our <Link to="/privacy-policy" className="cc-link">Cookie Policy</Link>.
          </p>
        </div>
        <div className="cc-actions">
          <button type="button" className="cc-btn cc-btn--ghost" onClick={() => decide(false)}>
            Reject
          </button>
          <button type="button" className="cc-btn cc-btn--primary" onClick={() => decide(true)}>
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
